import * as R from 'ramda'
import * as RE from 'helpers/ramda-ext'
import { createSelector } from 'reselect'
import moment from 'moment'
import {
  getResourcePathname,
  isNotPrimaryNavigation,
  isPrimaryNavigation,
  isSaleKey,
  isTypeNavigation,
  isTypeProduct,
  isTypeSale
} from 'domain/resources'
import { concatMediaPathIfNeeded, toHref } from 'helpers/PathHelper'
import { ALL_FILTERS_KEYS, TRANSPORTATION_FILTERS_KEY } from 'constants/filters'
import { getResources } from 'store/reducers/resources'
import { getMediaPath, getOperationCode, getSaleKey } from './application'
import { getQuery } from './router'
import {
  CityWithKey,
  GetActiveSaleFiltersConf,
  GetActiveSaleFiltersURIParams,
  GetBannerImageUrl,
  GetBeginDate,
  GetBrandImageUrl,
  GetCities,
  GetDaysSinceStartSale,
  GetFavoriteDepartureCityCode,
  GetFirstProduct,
  GetFirstProductRedirectUri,
  GetHasMultipleProduct,
  GetHasOneProduct,
  GetHasOnlyOneUniverse,
  GetIsResourceFetched,
  GetIsSaleDescriptionActive,
  GetIsSaleEndDateActive,
  GetMarketingDescription,
  GetMomentedEndDate,
  GetNbSubUniversesInCatalog,
  GetNbUniversesInCatalog,
  GetNormalizedNbSubUniversesInCatalog,
  GetNormalizedNbUniversesInCatalog,
  GetProductsCountInCatalog,
  GetQueryFiltersParams,
  GetSaleCities,
  GetSaleContent,
  GetSaleCurrency,
  GetSaleCurrencyCode,
  GetSaleDescription,
  GetSaleFilters,
  GetSaleHasUniverse,
  GetSalePartner,
  GetSalePathname,
  GetSaleProvider,
  GetSaleResource,
  GetSaleSlug,
  GetSaleStartTime,
  GetSaleTitle,
  GetShowSaleFilters,
  GetTopDepartureCities,
  GetTopDepartureCitiesCode,
  GetTopDurationsCode,
  GetUniversesCountInCatalog,
  ProductResource,
  Query,
  QueryFiltersParams,
  Resources,
  SaleFiltersConf,
  SaleResource
} from './sale.types'

export const getIsResourceFetched: GetIsResourceFetched = createSelector(
  getResources,
  R.both(RE.isNotEmpty, RE.isNotNil)
)

export const getSaleResource: GetSaleResource = createSelector(
  getResources,
  getSaleKey,
  (allResources: Resources, saleKey: string) => allResources[saleKey] as SaleResource
)

export const getSaleSlug: GetSaleSlug = createSelector(
  getSaleResource,
  R.when(
    RE.isNotNil,
    R.prop('slug')
  )
)

export const getSaleFilters: GetSaleFilters = createSelector(
  getSaleResource,
  (sale) => R.compose(
    R.filter(R.equals(true)),
    R.pick(ALL_FILTERS_KEYS),
    R.defaultTo({}),
    R.path(['ecomm', 'filters'])
  )(sale)
)

export const getActiveSaleFiltersConf: GetActiveSaleFiltersConf = createSelector(
  getSaleFilters,
  R.compose(
    R.reduce((
      filterConf: SaleFiltersConf,
      filterKey: string
    ) => ({ ...filterConf, [filterKey]: { key: filterKey } }), {}),
    R.reverse,
    R.map(filterKey => ALL_FILTERS_KEYS.find(key => key === filterKey)),
    R.keys
  )
)

export const getActiveSaleFiltersURIParams: GetActiveSaleFiltersURIParams = createSelector(
  getActiveSaleFiltersConf,
  Object.keys
)

export const getShowSaleFilters: GetShowSaleFilters = createSelector(
  getSaleFilters,
  RE.isNotEmpty
)

export const getQueryFiltersParams: GetQueryFiltersParams = createSelector(
  getQuery,
  getActiveSaleFiltersURIParams,
  (query: Query, saleFiltersURIParam: string[]) => R
    .keys(query)
    .reduce((filterParams: QueryFiltersParams, filter: string) => {
      if (R.contains(filter, [...saleFiltersURIParam, TRANSPORTATION_FILTERS_KEY])) {
        filterParams[filter] = query[filter]
      }

      return filterParams
    }, {})
)

export const getFirstProduct: GetFirstProduct = createSelector(
  getSaleKey,
  getResources,
  (saleKey: string, allResources: Resources) => R.compose(
    R.find(
      R.both(isTypeProduct, isSaleKey(saleKey))
    ),
    R.values
  )(allResources)
)

export const getSaleHasUniverse: GetSaleHasUniverse = createSelector(
  getSaleResource,
  getResources,
  (sale: SaleResource, all: Resources) => {
    if (!all || !sale) return false

    return R.compose(
      R.complement(R.isNil),
      R.find(isTypeNavigation),
      R.map(R.prop(R.__, all)),
      R.prop('resources')
    )(sale)
  }
)

export const getSaleTitle: GetSaleTitle = createSelector(
  getSaleResource,
  R.ifElse(
    R.isNil,
    R.always(null),
    R.path(['content', 'title'])
  )
)

export const getSaleCurrency: GetSaleCurrency = createSelector(
  getSaleResource,
  sale => sale?.content?.currency
)

export const getSaleCurrencyCode: GetSaleCurrencyCode = createSelector(
  getSaleCurrency,
  currency => currency?.code
)

export const getProductsCountInCatalog: GetProductsCountInCatalog = createSelector(
  getSaleKey,
  getResources,
  (saleKey: string, allResources: Resources) => R.compose(
    R.length,
    R.keys,
    R.filter(R.both(isTypeProduct, isSaleKey(saleKey)))
  )(allResources)
)

export const getUniversesCountInCatalog: GetUniversesCountInCatalog = createSelector(
  getSaleKey,
  getResources,
  (saleKey: string, allResources: Resources) => R.ifElse(
    R.isEmpty,
    R.always(0),
    R.compose(
      R.length,
      R.filter(R.both(isTypeNavigation, isSaleKey(saleKey))),
      R.values
    )
  )(allResources)
)

export const getNbUniversesInCatalog: GetNbUniversesInCatalog = createSelector(
  getSaleKey,
  getResources,
  (saleKey: string, allResources: Resources) => R.ifElse(
    R.isEmpty,
    R.always(0),
    R.compose(
      R.length,
      R.filter(R.allPass([isTypeNavigation, isPrimaryNavigation, isSaleKey(saleKey)])),
      R.values
    )
  )(allResources)
)

export const getNormalizedNbUniversesInCatalog: GetNormalizedNbUniversesInCatalog = createSelector(
  getNbUniversesInCatalog,
  R.ifElse(
    R.lt(1),
    R.identity,
    R.always(0)
  )
)

export const getNbSubUniversesInCatalog: GetNbSubUniversesInCatalog = createSelector(
  getSaleKey,
  getResources,
  (saleKey: string, allResources: Resources) => R.ifElse(
    R.isEmpty,
    R.always(0),
    R.compose(
      R.length,
      R.filter(R.allPass([isTypeNavigation, isNotPrimaryNavigation, isSaleKey(saleKey)])),
      R.values
    )
  )(allResources)
)

export const getNormalizedNbSubUniversesInCatalog: GetNormalizedNbSubUniversesInCatalog = createSelector(
  getNormalizedNbUniversesInCatalog,
  getNbSubUniversesInCatalog,
  (nbUniverses, nbSubUniverses) => R.ifElse(
    R.lt(1),
    R.always(nbSubUniverses),
    R.always(0)
  )(nbUniverses)
)

export const getHasMultipleProduct: GetHasMultipleProduct = createSelector(
  getProductsCountInCatalog,
  R.gt(R.__, 1)
)

export const getHasOneProduct: GetHasOneProduct = createSelector(
  getProductsCountInCatalog,
  R.equals(1)
)

export const getHasOnlyOneUniverse: GetHasOnlyOneUniverse = createSelector(
  getUniversesCountInCatalog,
  R.equals(1)
)

export const getFirstProductRedirectUri: GetFirstProductRedirectUri = createSelector(
  getFirstProduct,
  getResources,
  getOperationCode,
  getQueryFiltersParams,
  getQuery,
  (product: ProductResource, allResources: Resources, opeCode: string, filters: QueryFiltersParams, query: Query) => (product)
    ? toHref({
      pathname: getResourcePathname(opeCode, allResources, allResources[product.parent], product),
      query: { key: product.key, ...filters }
    }, query)
    : null
)

export const getBannerImageUrl: GetBannerImageUrl = createSelector(
  getMediaPath,
  R.compose(R.path(['content', 'background']), getSaleResource),
  concatMediaPathIfNeeded
)

export const getBrandImageUrl: GetBrandImageUrl = createSelector(
  getMediaPath,
  R.compose(R.path(['content', 'logo']), getSaleResource),
  concatMediaPathIfNeeded
)

export const getSalePathname: GetSalePathname = createSelector(
  getSaleSlug,
  getOperationCode,
  (saleSlug: string, opeCode: string) => `/${opeCode}/${saleSlug}`
)

export const getCities: GetCities = createSelector(
  getSaleResource,
  R.pipe(
    R.path(['ecomm', 'cities']),
    R.defaultTo([]),
    R.map(RE.renameKey('code', 'key'))
  )
)

export const getBeginDate: GetBeginDate = createSelector(
  getSaleResource,
  R.path(['ecomm', 'beginDate'])
)

export const getMomentedEndDate: GetMomentedEndDate = createSelector(
  getSaleResource,
  R.compose(
    moment,
    R.path(['ecomm', 'endDate'])
  )
)

export const getSaleStartTime: GetSaleStartTime = createSelector(
  getBeginDate,
  beginDate => moment(beginDate).format('hhA')
)

export const getDaysSinceStartSale: GetDaysSinceStartSale = createSelector(
  getBeginDate,
  beginDate => moment().startOf('day').diff(moment(beginDate).startOf('day'), 'days')
)

export const getSaleProvider: GetSaleProvider = createSelector(
  getSaleResource,
  R.path(['ecomm', 'provider'])
)

export const getFavoriteDepartureCityCode: GetFavoriteDepartureCityCode = createSelector(
  getSaleResource,
  R.path(['ecomm', 'marketDepartureCity', 'code'])
)

export const getSaleContent: GetSaleContent = createSelector(
  getSaleResource,
  R.prop('content')
)

export const getSaleDescription: GetSaleDescription = createSelector(
  getSaleContent,
  R.prop('saleDescription')
)

export const getMarketingDescription: GetMarketingDescription = createSelector(
  getSaleContent,
  R.prop('marketingDescription')
)

export const getSaleCities: GetSaleCities = createSelector(
  getSaleResource,
  R.path(['ecomm', 'cities'])
)

export const getIsSaleDescriptionActive: GetIsSaleDescriptionActive = createSelector(
  getSaleDescription,
  saleDescription => Boolean(
    saleDescription && (
      saleDescription.description ?? saleDescription.title
    )
  )
)

export const getIsSaleEndDateActive: GetIsSaleEndDateActive = createSelector(
  getSaleResource,
  getMomentedEndDate,
  (resource, endDate) =>
    R.path(['ecomm', 'shouldDisplaySaleEndDate'])(resource) &&
    endDate > moment()
)

export const getTopDepartureCitiesCode: GetTopDepartureCitiesCode = createSelector(
  getSaleResource,
  R.pipe(
    R.path(['ecomm', 'topCities']),
    R.defaultTo([])
  )
)

export const getTopDepartureCities: GetTopDepartureCities = createSelector(
  getTopDepartureCitiesCode,
  getCities,
  (topCodes, cities) =>
    topCodes.reduce((acc: CityWithKey[], code: string) => {
      const foundCity: CityWithKey | undefined = cities.find(city => city.key === code)

      if (foundCity) {
        acc.push(foundCity)
      }

      return acc
    }, [])
)

export const getTopDurationsCode: GetTopDurationsCode = createSelector(
  getSaleResource,
  R.path(['ecomm', 'topDurations'])
)

export const getSalePartner: GetSalePartner = createSelector(
  getSaleKey,
  getResources,
  (saleKey: string, allResources: Resources) => {
    const singlePartnerLabel = R.compose(
      R.uniq,
      R.map(R.path(['ecomm', 'merchantLabel'])),
      R.values,
      R.filter(R.both(isTypeProduct, isSaleKey(saleKey)))
    )(allResources)?.[0] ?? false

    const isMultiPartner = R.compose(
      R.uniq,
      R.map(R.path(['ecomm', 'isMultiPartner'])),
      R.values,
      R.filter(isTypeSale)
    )(allResources)?.[0]
    return { isMultiPartner, singlePartnerLabel }
  }

)
